import {
	type Block,
	type MonoBitmap,
	type PaperWidth,
	type PrintTransport,
	PrintTransportError,
	qrPath,
} from "@luckymenu/staff-core";

/**
 * The browser print dialog (`system`, printing.md, Transports #5): the last
 * resort when Print Bridge is not installed. It cannot take ESC/POS, so it
 * prints the document itself, drawn as HTML with a receipt stylesheet sized
 * to the paper, in a hidden frame. No cut or drawer control; QR codes print
 * as vector images, logos as pictures. The browser that set the printer up
 * prints for it (its `address` is that browser's device ID).
 */

const PRINTABLE_MM: Record<PaperWidth, number> = { 80: 72, 58: 48 };

const escapeHtml = (text: string) =>
	text.replace(
		/[&<>"']/g,
		(c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c,
	);

function bitmapUrl(bitmap: MonoBitmap): string {
	const canvas = document.createElement("canvas");
	canvas.width = bitmap.width;
	canvas.height = bitmap.height;
	const ctx = canvas.getContext("2d");
	if (!ctx) return "";
	const image = ctx.createImageData(bitmap.width, bitmap.height);
	const bytesPerRow = Math.ceil(bitmap.width / 8);
	for (let y = 0; y < bitmap.height; y++) {
		for (let x = 0; x < bitmap.width; x++) {
			const black = ((bitmap.data[y * bytesPerRow + (x >> 3)] ?? 0) & (0x80 >> (x & 7))) !== 0;
			const i = (y * bitmap.width + x) * 4;
			const v = black ? 0 : 255;
			image.data[i] = v;
			image.data[i + 1] = v;
			image.data[i + 2] = v;
			image.data[i + 3] = 255;
		}
	}
	ctx.putImageData(image, 0, 0);
	return canvas.toDataURL("image/png");
}

type Style = NonNullable<Extract<Block, { t: "text" }>["style"]>;

function styleOf(style: Style | undefined): string {
	if (!style) return "";
	const css: string[] = [];
	if (style.align) css.push(`text-align:${style.align}`);
	if (style.bold) css.push("font-weight:700");
	if (style.underline) css.push("text-decoration:underline");
	if (style.invert) css.push("background:#000;color:#fff");
	const h = style.h ?? 1;
	const w = style.w ?? 1;
	const base = style.font === "B" ? 0.8 : 1;
	if (h !== 1 || base !== 1) css.push(`font-size:${(base * h).toFixed(2)}em`);
	if (w !== h) css.push(`letter-spacing:${((w - h) * 0.3).toFixed(2)}em`);
	return css.join(";");
}

/** The document as receipt HTML. */
export function receiptHtml(blocks: Block[], paper: PaperWidth, title: string): string {
	const body: string[] = [];
	for (const b of blocks) {
		switch (b.t) {
			case "text":
				body.push(`<p style="${styleOf(b.style)}">${escapeHtml(b.text) || "&nbsp;"}</p>`);
				break;
			case "row": {
				const total = b.cols.reduce((n, c) => n + c.weight, 0);
				const cells = b.cols
					.map(
						(c) =>
							`<span style="flex:0 0 ${((c.weight / total) * 100).toFixed(3)}%;text-align:${c.align ?? "left"}">${escapeHtml(c.text)}</span>`,
					)
					.join("");
				body.push(`<div class="row" style="${styleOf(b.style)}">${cells}</div>`);
				break;
			}
			case "rule":
				body.push(`<hr class="${b.ch === "=" ? "double" : "dashed"}">`);
				break;
			case "feed":
				body.push(`<div style="height:${b.lines * 1.2}em"></div>`);
				break;
			case "qr": {
				const { size, d } = qrPath(b.data);
				const mm = Math.min(PRINTABLE_MM[paper], (b.size ?? 6) * 6);
				body.push(
					`<div style="text-align:${b.align ?? "center"}"><svg viewBox="0 0 ${size} ${size}" style="width:${mm}mm;height:${mm}mm" shape-rendering="crispEdges"><path d="${d}"/></svg></div>`,
				);
				break;
			}
			case "barcode":
				body.push(`<p style="text-align:${b.align ?? "center"}">${escapeHtml(b.data)}</p>`);
				break;
			case "bitmap":
				body.push(
					`<div style="text-align:${b.align ?? "center"}"><img alt="" src="${bitmapUrl(b.bitmap)}" style="max-width:100%;image-rendering:pixelated"></div>`,
				);
				break;
			default:
				// Cut, beep, drawer: the dialog cannot do them. Logos arrive as bitmaps.
				break;
		}
	}
	const width = PRINTABLE_MM[paper];
	return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
@page { size: ${paper}mm auto; margin: 0; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #fff; color: #000; }
body { width: ${width}mm; margin: 0 auto; padding: 2mm 0 6mm; font: 11px/1.3 "Noto Sans Mono", "DejaVu Sans Mono", Menlo, Consolas, monospace; }
p { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
.row { display: flex; gap: 0; }
.row > span { white-space: pre-wrap; overflow-wrap: anywhere; padding-right: 0.5ch; }
.row > span:last-child { padding-right: 0; }
hr { border: 0; margin: 0.35em 0; }
hr.dashed { border-top: 1px dashed #000; }
hr.double { border-top: 3px double #000; }
svg path { fill: #000; }
</style></head><body>${body.join("")}</body></html>`;
}

/** Prints `html` through the dialog in a hidden frame; resolves when the dialog closes. */
function printHtml(html: string): Promise<void> {
	return new Promise((resolve, reject) => {
		const frame = document.createElement("iframe");
		frame.setAttribute("aria-hidden", "true");
		frame.style.cssText =
			"position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
		let done = false;
		const finish = () => {
			if (done) return;
			done = true;
			// Some browsers print asynchronously after print() returns.
			setTimeout(() => frame.remove(), 1000);
			resolve();
		};
		frame.onload = () => {
			const win = frame.contentWindow;
			if (!win) {
				frame.remove();
				reject(new PrintTransportError("unreachable", "The print dialog could not open"));
				return;
			}
			win.addEventListener("afterprint", finish, { once: true });
			try {
				win.focus();
				win.print();
				// print() returns once the dialog closes in Chrome and Firefox.
				setTimeout(finish, 500);
			} catch (error) {
				frame.remove();
				reject(new PrintTransportError("unreachable", String(error)));
			}
		};
		frame.srcdoc = html;
		document.body.appendChild(frame);
	});
}

export function systemPrintTransport(): PrintTransport {
	return {
		connection: "system",
		send() {
			return Promise.reject(
				new PrintTransportError("not_supported", "The print dialog takes documents, not bytes"),
			);
		},
		printDocument(blocks, paper, title) {
			return printHtml(receiptHtml(blocks, paper, title));
		},
	};
}
